/**
 * Seven static paths. No parameters, no nesting, no loaders.
 *
 * WHY REAL PATHS AND NOT A HASH ROUTER: the API's OAuth callbacks redirect the
 * browser to https://<admin origin>/tabelle?state=… and /ebay?state=…
 * (api/src/adminRoutes.ts's adminRedirect). Those are server-issued absolute
 * URLs; a hash router would land on the wrong place. Deep links work because
 * package 3b's assets config uses not_found_handling:
 * "single-page-application".
 *
 * WHY NOT react-router: this table is the whole routing problem. See the
 * dependency table in Task 1.
 */

export const ROUTES = {
  start: '/',
  staff: '/mitarbeiter',
  sheet: '/tabelle',
  masterData: '/stammdaten',
  ebay: '/ebay',
  jobs: '/jobs',
  login: '/anmelden',
  notFound: '/404',
} as const;

export type RouteId = keyof typeof ROUTES;

const BY_PATH = new Map<string, RouteId>(
  (Object.entries(ROUTES) as [RouteId, string][])
    .filter(([id]) => id !== 'notFound')
    .map(([id, path]) => [path, id]),
);

export function matchRoute(pathname: string): RouteId {
  // One trailing slash is the same route; anything deeper is not. '/jobs/42'
  // must be notFound rather than 'jobs' -- there is no detail view, and
  // pretending otherwise hides a broken link.
  const normalized = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return BY_PATH.get(normalized) ?? 'notFound';
}

export function pathFor(id: RouteId): string {
  return ROUTES[id];
}

/**
 * The slice of a MouseEvent an in-app anchor's click handler reads. Structural
 * on purpose: React's `MouseEvent<HTMLAnchorElement>` satisfies it, and so does
 * an object literal, so the rule below is exhaustively drivable from Node with
 * no renderer and no jsdom.
 */
export interface NavClick {
  defaultPrevented: boolean;
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
}

/**
 * MAY THIS CLICK BE TURNED INTO AN IN-APP NAVIGATION?
 *
 * Every nav anchor in this SPA carries a REAL `href` and is intercepted only
 * for a plain left click. The four modifiers each mean something the browser
 * does better than we do, and swallowing any of them takes a capability away
 * from the operator with no way to get it back:
 *
 *   meta / ctrl -> open in a new tab      shift -> open in a new window
 *   alt         -> download the target    button !== 0 -> middle/right click
 *
 * `defaultPrevented` is first because a handler further in has already decided.
 *
 * ONE FUNCTION, SIX CALL SITES (final review m-1). This body was hand-copied
 * into Layout, Start, MasterData twice, Staff and Jobs, and the copy in
 * `views/Jobs.tsx` had drifted -- it checked meta and ctrl only, so on `/jobs`
 * and nowhere else shift-click and alt-click were silently swallowed. The
 * review's M20 then narrowed Layout's copy to match Jobs' and survived all
 * 1096 tests, which is why the drift went unnoticed for six tasks: the
 * convention was unpinned in BOTH directions. It is one function now, and
 * test/router.test.ts drives all six refusal cases plus the accept case over
 * it.
 */
export function isPlainLeftClick(event: NavClick): boolean {
  if (event.defaultPrevented || event.button !== 0) return false;
  return !(event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);
}

/**
 * The two query parameters the OAuth landings read on /tabelle and /ebay:
 * api/src/adminRoutes.ts's adminRedirect appends ?state=<jti> on success and
 * ?error=<code> on failure. Pure and unit-tested here — deliberately not
 * left inline in Router.tsx — because Router.tsx gets no dedicated DOM suite
 * under this plan's DOM-test-scope policy (it's not one of the three), so a
 * regression in query preservation would otherwise only surface via a much
 * later task's DOM test, if at all.
 */
export interface LandingQuery {
  state?: string;
  error?: string;
}

export function parseLanding(search: string): LandingQuery {
  const params = new URLSearchParams(search);
  const landing: LandingQuery = {};
  const state = params.get('state');
  if (state !== null) landing.state = state;
  const error = params.get('error');
  if (error !== null) landing.error = error;
  return landing;
}

/**
 * THE STAFF VIEW'S TAB IS PART OF THE URL (Task 6 review m-6, carried forward).
 *
 * /mitarbeiter carries three tabs on one route, so "which tab" has to be
 * addressable or it is not a location at all: a reload, a bookmark, a link
 * pasted into a support thread and the browser's Back button would all land
 * on whatever the component happened to initialise its useState to.
 *
 * THE FRAGMENT, NOT A QUERY PARAMETER, for two reasons that are not taste:
 *
 *  1. Layout.tsx ALREADY ships `/mitarbeiter#administratoren` as the
 *     owner-only nav entry (its ADMIN_USERS_HREF, written in Task 6 and
 *     pinned by DOM suite #1). Choosing `?tab=` here would leave that nav
 *     link pointing at a fragment nothing reads -- a dead control in the
 *     shell, discovered by an owner rather than by a test.
 *  2. `?` is spoken for on this origin: adminRedirect bounces the OAuth
 *     landings back with ?state=<jti> and ?error=<code>, and `clearSearch`
 *     strips the whole query string without a navigation (Router.tsx). A tab
 *     living in the query would be collateral of that strip.
 *
 * Parsing is HERE and pure, per the same rule `parseLanding` follows: the
 * component calls it, never re-derives it. Which tabs are actually offered
 * is a capability question, not a URL question -- that stays in
 * lib/staffView.ts's `visibleStaffTabs`/`resolveStaffTab`, so an unknown OR
 * a forbidden fragment both fall back rather than rendering an owner-only
 * panel to a manager.
 */
export const STAFF_TABS = ['staff', 'devices', 'adminUsers'] as const;
export type StaffTab = (typeof STAFF_TABS)[number];

/** German fragments: they are user-visible URL text on a German-only surface. */
const STAFF_TAB_FRAGMENT: Record<StaffTab, string> = {
  staff: 'mitarbeiter',
  devices: 'geraete',
  // Byte-identical to Layout.tsx's ADMIN_USERS_HREF fragment. Changing one
  // without the other silently breaks the shell's owner-only nav entry.
  adminUsers: 'administratoren',
};

const STAFF_TAB_BY_FRAGMENT = new Map<string, StaffTab>(
  (Object.entries(STAFF_TAB_FRAGMENT) as [StaffTab, string][]).map(([id, fragment]) => [fragment, id]),
);

/** '' | '#geraete' | '#Geraete' | garbage -> a tab. Never throws: a malformed
 *  percent-escape in a hand-edited URL must open the first tab, not a blank
 *  screen from an uncaught URIError. */
export function parseStaffTab(hash: string): StaffTab {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  let fragment: string;
  try {
    fragment = decodeURIComponent(raw);
  } catch {
    fragment = raw;
  }
  return STAFF_TAB_BY_FRAGMENT.get(fragment.toLowerCase()) ?? 'staff';
}

export function staffTabHref(tab: StaffTab): string {
  return `${ROUTES.staff}#${STAFF_TAB_FRAGMENT[tab]}`;
}

/**
 * `/stammdaten` CARRIES THREE SECTIONS ON ONE ROUTE, the same shape as
 * `/mitarbeiter` (Task 9's m-6 decision, carried forward): a reload, a
 * bookmark or a pasted link must land back on the same section, so the
 * section is a location, not `useState`'s initial value.
 *
 * No `resolveMasterDataTab` is needed here, unlike the staff view's
 * `resolveStaffTab`: capabilities.ts gates `masterData` on role.manager-and-up
 * only, never per-section, so every tab is visible to anyone who reaches this
 * route at all -- there is no forbidden fragment to fall back away from, only
 * an unknown one.
 */
export const MASTER_DATA_TABS = ['locations', 'lists', 'settings'] as const;
export type MasterDataTab = (typeof MASTER_DATA_TABS)[number];

/** German fragments: user-visible URL text on a German-only surface. */
const MASTER_DATA_TAB_FRAGMENT: Record<MasterDataTab, string> = {
  locations: 'standorte',
  lists: 'listen',
  settings: 'einstellungen',
};

const MASTER_DATA_TAB_BY_FRAGMENT = new Map<string, MasterDataTab>(
  (Object.entries(MASTER_DATA_TAB_FRAGMENT) as [MasterDataTab, string][]).map(([id, fragment]) => [fragment, id]),
);

/** Same shape as `parseStaffTab`: never throws, an unknown or malformed
 *  fragment opens the first section rather than a blank screen. */
export function parseMasterDataTab(hash: string): MasterDataTab {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  let fragment: string;
  try {
    fragment = decodeURIComponent(raw);
  } catch {
    fragment = raw;
  }
  return MASTER_DATA_TAB_BY_FRAGMENT.get(fragment.toLowerCase()) ?? 'locations';
}

export function masterDataTabHref(tab: MasterDataTab): string {
  return `${ROUTES.masterData}#${MASTER_DATA_TAB_FRAGMENT[tab]}`;
}

/**
 * `/jobs` CARRIES TWO PANELS ON ONE ROUTE (admin spec 4.3), the same shape as
 * `/mitarbeiter` and `/stammdaten` and for the same reason: a reload, a
 * bookmark or a link pasted into a support thread must land back on the panel
 * it named, so the panel is a location and not `useState`'s initial value. It
 * matters more here than on the other two -- "look at the Sheets chart" is a
 * sentence one operator sends another.
 *
 * The MONITOR AND THE ACTIONS SHARE ONE TAB deliberately: the buttons create
 * the rows the table shows, and a `409 job_running` scrolls to an existing row
 * (lib/jobsView.ts's `focusJob`), which it can only do if that row is mounted.
 *
 * No `resolveJobsTab` is needed, unlike the staff view's `resolveStaffTab`:
 * capabilities.ts gates `jobs` and `metrics` on the manager role only, which
 * the whole route already requires, so there is no forbidden fragment to fall
 * back away from -- only an unknown one.
 */
export const JOBS_TABS = ['jobs', 'metrics'] as const;
export type JobsTab = (typeof JOBS_TABS)[number];

/** German fragments: user-visible URL text on a German-only surface. */
const JOBS_TAB_FRAGMENT: Record<JobsTab, string> = {
  jobs: 'jobs',
  metrics: 'metriken',
};

const JOBS_TAB_BY_FRAGMENT = new Map<string, JobsTab>(
  (Object.entries(JOBS_TAB_FRAGMENT) as [JobsTab, string][]).map(([id, fragment]) => [fragment, id]),
);

/** Same shape as `parseStaffTab`: never throws, an unknown or malformed
 *  fragment opens the first panel rather than a blank screen. */
export function parseJobsTab(hash: string): JobsTab {
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  let fragment: string;
  try {
    fragment = decodeURIComponent(raw);
  } catch {
    fragment = raw;
  }
  return JOBS_TAB_BY_FRAGMENT.get(fragment.toLowerCase()) ?? 'jobs';
}

export function jobsTabHref(tab: JobsTab): string {
  return `${ROUTES.jobs}#${JOBS_TAB_FRAGMENT[tab]}`;
}
